import { useEffect, useRef, useState, type ReactNode } from "react";
import { usePrefersReducedMotion } from "../hooks/usePrefersReducedMotion";

type DragCarouselProps = {
  children: ReactNode;
  className?: string;
  /** Cienka linia postępu pod karuzelą - zastępuje kreski paginacji */
  showProgress?: boolean;
  accent?: string;
  gapPx?: number;
};

/**
 * Poziomy drag-scroll z bezwładnością - zastępuje karuzelę z 9 kreskami
 * paginacji w sekcji Opinie. Karty zostają dokładnie te same, zmienia się
 * tylko kontener.
 *
 * - Mysz: chwyć i przeciągnij (cursor grab/grabbing), po puszczeniu momentum
 *   z wygaszaniem; scroll-snap dociąga do karty po zatrzymaniu.
 * - Dotyk: w ogóle nie przechwytujemy - natywny scroll telefonu jest lepszy
 *   niż jakakolwiek imitacja.
 * - data-lenis-prevent: Lenis nie przejmuje scrolla wewnątrz karuzeli.
 * - Klik po przeciągnięciu > 6px jest tłumiony (nie odpala linków w kartach).
 * - Reduced-motion: drag działa, momentum wyłączone.
 *
 * Użycie (sekcja #reviews):
 *   <DragCarousel>
 *     {opinie.map(o => <ReviewCard key={o.id} {...o} className="w-[380px]" />)}
 *   </DragCarousel>
 * Każda karta potrzebuje stałej szerokości (klasa w-[...] jak wyżej).
 * Style kontenera (ukrycie scrollbara, snap dzieci) są w phase2.css (.pr-carousel).
 */
export function DragCarousel({ children, className, showProgress = true, accent = "#BEF264", gapPx = 24 }: DragCarouselProps) {
  const trackRef = useRef<HTMLDivElement>(null);
  const reducedMotion = usePrefersReducedMotion();
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    const track = trackRef.current;
    if (!track) return;

    let isDown = false;
    let dragged = false;
    let startX = 0;
    let startScroll = 0;
    let lastX = 0;
    let lastT = 0;
    let velocity = 0;
    let momentumRaf = 0;

    const onScroll = () => {
      const max = track.scrollWidth - track.clientWidth;
      setProgress(max > 0 ? track.scrollLeft / max : 0);
    };

    const stopMomentum = () => cancelAnimationFrame(momentumRaf);

    const onPointerDown = (e: PointerEvent) => {
      if (e.pointerType !== "mouse") return; // dotyk: natywny scroll
      isDown = true;
      dragged = false;
      stopMomentum();
      startX = e.clientX;
      startScroll = track.scrollLeft;
      lastX = e.clientX;
      lastT = performance.now();
      velocity = 0;
      track.style.cursor = "grabbing";
    };

    const onPointerMove = (e: PointerEvent) => {
      if (!isDown) return;
      const dx = e.clientX - startX;
      // Capture + traktuj jako drag DOPIERO po przekroczeniu progu - dzięki temu
      // zwykły klik (bez ruchu) dochodzi do elementów wewnątrz karty (np. "czytaj więcej").
      if (!dragged && Math.abs(dx) > 6) {
        dragged = true;
        track.style.scrollSnapType = "none";
        track.style.userSelect = "none";
        try {
          track.setPointerCapture(e.pointerId);
        } catch {
          /* no-op */
        }
      }
      if (!dragged) return;
      track.scrollLeft = startScroll - dx;
      const now = performance.now();
      const dt = now - lastT || 16;
      velocity = ((e.clientX - lastX) / dt) * 16; // px na klatkę @60fps
      lastX = e.clientX;
      lastT = now;
    };

    const endDrag = () => {
      if (!isDown) return;
      isDown = false;
      track.style.cursor = "grab";
      track.style.userSelect = "";

      if (reducedMotion || Math.abs(velocity) < 1) {
        track.style.scrollSnapType = "";
        return;
      }
      let v = velocity;
      const momentum = () => {
        track.scrollLeft -= v;
        v *= 0.94;
        if (Math.abs(v) > 0.4) {
          momentumRaf = requestAnimationFrame(momentum);
        } else {
          track.style.scrollSnapType = "";
        }
      };
      momentumRaf = requestAnimationFrame(momentum);
    };

    // Tłumienie klików po przeciągnięciu - linki w kartach nie odpalają się przypadkiem
    const onClickCapture = (e: MouseEvent) => {
      if (dragged) {
        e.preventDefault();
        e.stopPropagation();
        dragged = false;
      }
    };

    track.addEventListener("scroll", onScroll, { passive: true });
    track.addEventListener("pointerdown", onPointerDown);
    track.addEventListener("pointermove", onPointerMove);
    track.addEventListener("pointerup", endDrag);
    track.addEventListener("pointerleave", endDrag);
    track.addEventListener("click", onClickCapture, true);
    onScroll();

    return () => {
      stopMomentum();
      track.removeEventListener("scroll", onScroll);
      track.removeEventListener("pointerdown", onPointerDown);
      track.removeEventListener("pointermove", onPointerMove);
      track.removeEventListener("pointerup", endDrag);
      track.removeEventListener("pointerleave", endDrag);
      track.removeEventListener("click", onClickCapture, true);
    };
  }, [reducedMotion]);

  return (
    <div className={className}>
      <div
        ref={trackRef}
        className="pr-carousel"
        data-lenis-prevent
        tabIndex={0}
        aria-label="Opinie - przewiń poziomo"
        style={{ display: "flex", gap: gapPx, overflowX: "auto", scrollSnapType: "x mandatory", cursor: "grab" }}
      >
        {children}
      </div>

      {showProgress && (
        <div aria-hidden="true" style={{ marginTop: 28, height: 2, width: "100%", background: "rgba(255,255,255,0.1)", overflow: "hidden" }}>
          <div
            style={{
              height: "100%",
              width: "25%",
              background: accent,
              transform: `translateX(${progress * 300}%)`,
              transition: "transform 80ms linear",
            }}
          />
        </div>
      )}
    </div>
  );
}
